// AdminSubdomainTabs.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { FolderCog, Ellipsis } from "lucide-react";
import MyContext from "../../../MyContext";
import DomainTab from "./Domains";

const AdminSubdomainTabs = () => {
  const { baseUrl, config } = useContext(MyContext);

  // Tabs
  const [activeTab, setActiveTab] = useState("domain");

  // Domain-scoped state (API layer stays here)
  const [domain, setDomain] = useState("");
  const [subdomains, setSubdomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyById, setBusyById] = useState({});

  // Create form state
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");
  // Predefined IP choices for the Content (IP) select
const IP_OPTIONS = [
  "35.211.141.136", // your example
  "35.200.161.138", // existing example from UI placeholder
];

// Strict IPv4 validator (0–255 per octet)
const isValidIPv4 = (str) => {
  if (!str || typeof str !== "string") return false;
  // quick shape check
  const m = str.match(/^(\d{1,3}\.){3}\d{1,3}$/);
  if (!m) return false;
  // range check
  return str.split(".").every((o) => {
    const n = Number(o);
    return o === String(n) && n >= 0 && n <= 255;
  });
};
const isIpLike = isValidIPv4(newContent);

useEffect(() => {
  if (!newContent) setNewContent(IP_OPTIONS[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const ENDPOINTS = useMemo(
    () => ({
      create: `${baseUrl}/api/Admin/Domain/v1.0/createSubDomain`,
      list: `${baseUrl}/api/Admin/Domain/v1.0/getSubDomain`,
      del: `${baseUrl}/api/Admin/Domain/v1.0/deleteSubDomain`,
    }),
    [baseUrl]
  );

  // ONLY call this from the Fetch button
// --- add these states near the top with others ---
const [resultInfo, setResultInfo] = useState(null);
const [summary, setSummary] = useState({ code: "", message: "", success: null });

// --- replace fetchSubdomains with this ---
const fetchSubdomains = async (silent = false) => {
  if (!domain?.trim()) {
    if (!silent) toast.info("Enter a domain then click Fetch.");
    return;
  }
  if (!silent) setLoading(true);
  try {
    const res = await axios.post(ENDPOINTS.list, { domain: domain.trim() }, config);

    const d = res?.data;
    setSummary({
      code: d?.responseCode ?? "",
      message: d?.responseMessage ?? "",
      success: d?.data?.success ?? null,
    });

    // the list you want to render
    setSubdomains(d?.data?.result || []);

    // result_info for the top meta
    setResultInfo(d?.data?.result_info || null);
  } catch (e) {
    console.error("GetSubDomain error:", e);
    toast.error("Error fetching subdomains");
    setSubdomains([]);
    setResultInfo(null);
  } finally {
    if (!silent) setLoading(false);
  }
};


const handleCreate = async () => {
  if (!domain?.trim()) return toast.warn("Domain is required");
  if (!newName?.trim()) return toast.warn("Subdomain name is required");
  if (!newContent?.trim()) return toast.warn("Subdomain content is required");
  if (!isValidIPv4(newContent)) return toast.warn("Content must be a valid IPv4 (e.g., 35.211.141.136)");

  setCreating(true);
  try {
    const res = await axios.post(
      ENDPOINTS.create,
      {
        domain: domain.trim(),
        subdomainname: newName.trim(),
        subdomaincontent: newContent.trim(),
      },
      config
    );
    if (res.data?.responseCode === "00") {
      toast.success(res.data?.responseMessage || "Subdomain created");
      setNewName("");
      // keep current IP selection rather than clearing it
      await fetchSubdomains(true);
    } else {
      toast.error(res.data?.responseMessage || "Create failed");
    }
  } catch (e) {
    console.error("createSubDomain error:", e);
    toast.error("Error creating subdomain");
  } finally {
    setCreating(false);
  }
};


  const handleDelete = async (row) => {
    if (!row?.id) return;
    const resp = await Swal.fire({
      title: "Delete subdomain?",
      html: `<div style="text-align:left">
        <div><b>Domain:</b> ${row.domain || domain || "-"}</div>
        <div><b>Subdomain:</b> ${row.subdomainname || "-"}</div>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    });
    if (!resp.isConfirmed) return;

    try {
      setBusyById((p) => ({ ...p, [row.id]: true }));
      const res = await axios.post(
        ENDPOINTS.del,
        { domain: row.domain || domain, id: row.id },
        config
      );
      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Deleted");
        await fetchSubdomains(true);
      } else {
        toast.error(res.data?.responseMessage || "Delete failed");
      }
    } catch (e) {
      console.error("DeleteSubDomain error:", e);
      toast.error("Error deleting subdomain");
    } finally {
      setBusyById((p) => ({ ...p, [row.id]: false }));
    }
  };

  // useEffect(() => {
  //   if (domain && domain.length > 2) {
  //     const t = setTimeout(() => fetchSubdomains(true), 250);
  //     return () => clearTimeout(t);
  //   }
  // }, [domain]); 

  return (
    <div className="px-3 py-3 bg-white rounded-3 shadow-sm marginRight">
      {/* Header like client section */}
      <div className="admin-header d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="admin-header-icon">
            <FolderCog size={18} />
          </div>
          <div>
            <h2 className="m-0 admin-header-title">Chaafoo</h2>
            <div className="admin-header-sub">Subdomain Management & future tools</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs-wrap">
        <div className="admin-tabs-rail">
          <button
            type="button"
            className={`admin-tab ${activeTab === "domain" ? "active" : ""}`}
            onClick={() => setActiveTab("domain")}
          >
            <span>Domain</span>
          </button>
          <button
            type="button"
            className={`admin-tab ${activeTab === "more" ? "active" : ""}`}
            onClick={() => setActiveTab("more")}
          >
            <Ellipsis size={16} />
            <span>More</span>
          </button>

          {/* Active underline indicator */}
          <div
            className="admin-tab-underline"
            style={{
              transform:
                activeTab === "domain"
                  ? "translateX(0%)"
                  : "translateX(100%)",
            }}
          />
        </div>
      </div>

      {/* Tab body */}
      <div className="tab-content mt-3">
        {activeTab === "domain" && (
      <DomainTab
  domain={domain}
  subdomains={subdomains}
  resultInfo={resultInfo}
  summary={summary}
  loading={loading}
  creating={creating}
  busyById={busyById}
  newName={newName}
  newContent={newContent}
  isIpLike={isIpLike}               // ✅ NEW
  ipOptions={IP_OPTIONS}            // ✅ NEW
  setDomain={setDomain}
  setNewName={setNewName}
  setNewContent={setNewContent}
  onFetch={fetchSubdomains}
  onCreate={handleCreate}
  onDelete={handleDelete}
/>


        )}

        {activeTab === "more" && (
          <div className="border rounded p-4 text-muted bg-light-subtle">
            <h5 className="mb-2">More</h5>
            <p className="mb-0">
              Future tabs will appear here. Nothing to configure yet.
            </p>
          </div>
        )}
      </div>

      {/* Styles */}
      <style>{`
        /* Header */
        .admin-header-title{
          font-size: 22px;
          font-weight: 800;
          letter-spacing: .2px;
        }
        .admin-header-sub{
          color:#6b7280;
          font-size:.88rem;
          margin-top:2px;
        }
        .admin-header-icon{
          width:34px;height:34px;border-radius:10px;
          display:flex;align-items:center;justify-content:center;
          background: linear-gradient(180deg,#f7f7f9, #eceff3);
          border:1px solid #e5e7eb;
          color:#374151;
        }

        /* Tabs container */
        .admin-tabs-wrap{
          background: linear-gradient(180deg, rgba(246,248,250,.88), rgba(246,248,250,.65));
          border:1px solid #e6e8ec;
          padding:8px;
          border-radius:14px;
          box-shadow: 0 1px 2px rgba(0,0,0,.04), inset 0 1px 0 rgba(255,255,255,.8);
        }
        .admin-tabs-rail{
          position:relative;
          display:flex;
          gap:6px;
          background:#ffffff;
          border:1px solid #e9edf2;
          border-radius:10px;
          padding:6px;
          overflow-x:auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Tab pill */
        .admin-tab{
          position:relative;
          appearance:none;
          border:0;
          background:transparent;
          border-radius:8px;
          padding:10px 14px;
          font-weight:700;
          font-size:.92rem;
          color:#5b6472;
          display:inline-flex;
          align-items:center;
          gap:8px;
          transition: all .18s ease;
          white-space:nowrap;
        }
        .admin-tab:hover{ color:#111827; }
        .admin-tab.active{
          color:#111827;
          background: linear-gradient(180deg,#f3f6fb,#eef2f8);
          box-shadow: 0 1px 2px rgba(0,0,0,.04), inset 0 1px 0 rgba(255,255,255,.8);
        }

        /* Animated underline */
        .admin-tab-underline{
          position:absolute;
          bottom:-1px;
          left:0; width:50%;
          height:2px;
          background:#01C37D;
          border-radius:2px;
          transition: transform .22s ease;
        }

        /* Utility */
        .bg-light-subtle { background:#f8fafc; }

        /* Keep your existing admin-btn styles from previous module or reuse these: */
        .admin-btn{
          --bg:#f9fafb; --border:#d1d5db; --text:#111827;
          height:34px; padding:0 12px; font-size:.8rem; font-weight:600;
          border-radius:6px; border:1px solid var(--border);
          background:var(--bg); color:var(--text);
          display:inline-flex; align-items:center; gap:6px;
          transition:all .2s; cursor:pointer; white-space:nowrap;
        }
        .admin-btn:hover{ filter:brightness(1.05); transform:translateY(-1px); }
        .admin-btn:active{ transform:translateY(0); }
        .admin-btn.primary{ --bg:#01C37D; --border:#00a46a; --text:#fff; }
        .admin-btn.danger { --bg:#E74C3C; --border:#c0392b; --text:#fff; }
        .admin-btn.ghost  { --bg:#fff;    --border:#d1d5db; --text:#111827; }
      `}</style>

      <ToastContainer />
    </div>
  );
};

export default AdminSubdomainTabs;
